import { useState, type ReactNode } from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {
  ActionIcon,
  Badge,
  Button,
  Loader,
  LoadingOverlay,
  Menu,
  Paper,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { IconAlertCircle, IconCheck, IconChevronDown, IconRefresh } from '@tabler/icons-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_STYLES,
  BILLING_DISPLAY_TIMEZONE,
  mapAppointmentStatus,
} from '@/pages/Billing/utils'
import type { OrpcClient } from '@/orpc/client'
import { useSubmitClaim } from '@/hooks/useSubmitClaim'
import { usePractitioners } from '@/hooks/usePractitioners'
import { useIsManager } from '@/hooks/useIsManager'
import { resolveCurrentOrganizationId } from '@/utils/insuranceQuestionnaire'
import { formatRoutingLabel, PILL_STYLE_AMBER, PILL_STYLE_GREEN } from '../utils'
import { BillingEntityControl } from './BillingEntityControl'
import { ClaimStatusControl } from './ClaimStatusControl'
import { PaymentsCell } from './PaymentsCell'
import { RoutingControl } from './RoutingControl'
import { StatusPill } from './StatusPill'

dayjs.extend(utc)
dayjs.extend(timezone)

export type BillingCaseListItem = Awaited<
  ReturnType<OrpcClient['billingV2ListCases']>
>['items'][number]

const TABLE_HEADERS = [
  'Encounter / DOS',
  'Patient Name / DOB',
  'Address',
  'Visit Status',
  'Provider',
  'Insurance',
  'Insurance Status',
  'Plan',
  'Member ID',
  'Entity',
  'Progress Note',
  'Billing Codes',
  'Routing',
  'Payments',
  'Claim Status',
]

// Same summary semantics as the legacy Billing Codes column (the V2 case DTO
// carries no modifier): none → "+ Add codes", one line → "99204 · 2 diagnoses",
// multiple → "99204 + 2 more".
function formatBillingCodes(item: BillingCaseListItem): string {
  if (item.serviceLineCount === 0) {
    return '+ Add codes'
  }
  const code = item.primaryCode ?? '—'
  if (item.serviceLineCount === 1) {
    return `${code} · ${item.dxCount} ${item.dxCount === 1 ? 'diagnosis' : 'diagnoses'}`
  }
  return `${code} + ${item.serviceLineCount - 1} more`
}

// Legacy ADDRESS column split: line 1 = the street line, line 2 = the rest.
function addressLines(patientAddress: string | undefined): [string, string] | null {
  if (!patientAddress) return null
  const [first, ...rest] = patientAddress.split('\n')
  return [first, rest.join(', ')]
}

// Legacy blanks garbage member ids longer than 30 chars (coverageUtils rule).
function memberIdDisplay(memberId: string | undefined): string {
  if (!memberId || memberId.length > 30) return '—'
  return memberId
}

// Payer eligibility of the attached Coverage — the same verdict and words as the patient chart.
const PAYER_ELIGIBILITY_BADGES: Record<
  NonNullable<BillingCaseListItem['display']['insuranceStatus']>['payerEligibility'],
  { label: string; color: string }
> = {
  verified: { label: 'Verified', color: 'green' },
  'approved-manually': { label: 'Approved Manually', color: 'green' },
  disapproved: { label: 'Disapproved', color: 'red' },
  'not-verified': { label: 'Not Verified', color: 'red' },
  none: { label: 'No Coverage', color: 'gray' },
}

const CLINIC_DECISION_BADGES: Record<
  NonNullable<BillingCaseListItem['display']['insuranceStatus']>['clinicDecision'],
  { label: string; color: string }
> = {
  accepted: { label: 'Accepted by Clinic', color: 'green' },
  'not-accepted': { label: 'Not Accepted by Clinic', color: 'red' },
  none: { label: 'No Clinic Decision', color: 'gray' },
}

function InsuranceStatusCell({ item }: { item: BillingCaseListItem }) {
  const status = item.display.insuranceStatus
  if (!status) {
    return <span className="text-xs text-gray-400 hover:underline">No insurance attached</span>
  }
  const eligibility = PAYER_ELIGIBILITY_BADGES[status.payerEligibility]
  const decision = CLINIC_DECISION_BADGES[status.clinicDecision]
  return (
    <div className="flex flex-col gap-1">
      <Badge variant="light" radius="sm" color={eligibility.color}>
        {eligibility.label}
      </Badge>
      <Badge variant="light" radius="sm" color={decision.color}>
        {decision.label}
      </Badge>
    </div>
  )
}

function VisitStatusCell({ item }: { item: BillingCaseListItem }) {
  const status = mapAppointmentStatus(item.appointmentStatus ?? undefined)
  if (!status) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    )
  }
  return (
    <StatusPill style={APPOINTMENT_STATUS_STYLES[status]}>
      {APPOINTMENT_STATUS_LABELS[status].toUpperCase()}
    </StatusPill>
  )
}

// Derived exactly like legacy: a finished encounter means the progress note went out.
function ProgressNoteCell({ item }: { item: BillingCaseListItem }) {
  const isSubmitted = item.encounterStatus === 'finished'
  return (
    <StatusPill style={isSubmitted ? PILL_STYLE_GREEN : PILL_STYLE_AMBER}>
      {isSubmitted ? '✓ Submitted' : 'Not Submitted'}
    </StatusPill>
  )
}

// Routing cell: the route's visual (plain label, or the locked Managed-by badge once
// claimSubmittedAt is set — the clearinghouse owns the claim, PD-1739) is wrapped in
// RoutingControl, the biller's route picker (inert once an insurance payment is recorded).
// An external route (not none/manual — the same derivation the submit-claim handler uses,
// so the two never disagree) also drives the submission flow: a ready-to-bill case offers
// Submit claim and a failed one offers Retry (the recorded error in the tooltip); any
// other status is not submittable, so only the route chip renders. A submitted external
// case reads "Managed by <route>" on the chip.
function RoutingCell({ item }: { item: BillingCaseListItem }) {
  const label = formatRoutingLabel(item.submissionRoute)
  const isExternal = item.submissionRoute !== 'none' && item.submissionRoute !== 'manual'
  const isSubmittable = item.claimStatus === 'ready-to-bill' || item.claimStatus === 'failed'
  const offersSubmit = isExternal && !item.claimSubmittedAt && isSubmittable
  const submitClaim = useSubmitClaim(label)

  // "Submit as" (the split button's caret): another practitioner of the clinic — its
  // enrolled doctor when someone else rendered the visit. Per click, never stored on the
  // case, so a re-submit after a failure starts from the rendering provider again. The
  // list loads when the menu first opens: a practitioner belongs to one organization, so
  // the biller's own is the clinic's; the server still checks the pick against the case's.
  // Managers only: their access policy is the one that reads the clinic's other
  // practitioners, so for anyone else the caret would open an empty list.
  const { isManager } = useIsManager()
  const [submitAsOpened, setSubmitAsOpened] = useState(false)
  const medplum = useMedplum()
  const profileId = medplum.getProfile()?.id
  const { data: organizationId } = useQuery({
    queryKey: ['own-organization', profileId],
    enabled: isManager && submitAsOpened && Boolean(profileId),
    queryFn: () => resolveCurrentOrganizationId(medplum, profileId),
  })
  const { practitioners, isPending: practitionersPending } = usePractitioners(
    submitAsOpened && organizationId ? organizationId : '',
  )

  const submit = (submitAsPractitionerId?: string) =>
    submitClaim.mutate({
      encounterId: item.encounterId,
      ...(submitAsPractitionerId ? { submitAsPractitionerId } : {}),
    })

  const control = (
    <RoutingControl
      encounterId={item.encounterId}
      submissionRoute={item.submissionRoute}
      label={label}
      managed={isExternal && Boolean(item.claimSubmittedAt)}
      claimStatus={item.claimStatus}
      claimSubmittedAt={item.claimSubmittedAt}
      externalClaimId={item.externalClaimId}
      insurancePaidCents={item.payments.insurancePaidCents}
    />
  )

  if (!offersSubmit) {
    return control
  }

  // A split button: the main part submits under the rendering provider (the Claim's own);
  // the caret opens the "Submit as" list. One control, one visual weight — the override
  // is a step off the default path, not a second form field in the row.
  const mainButton = (
    <Button size="xs" loading={submitClaim.isPending} onClick={() => submit()}>
      {submitClaim.isPending
        ? 'Submitting…'
        : item.claimStatus === 'failed'
          ? 'Retry'
          : 'Submit claim'}
    </Button>
  )

  const submitButton =
    item.claimStatus === 'failed' && item.submitLastError ? (
      <Tooltip label={item.submitLastError} withArrow multiline w={260}>
        {mainButton}
      </Tooltip>
    ) : (
      mainButton
    )

  if (!isManager) {
    return (
      <div className="flex flex-col items-start gap-1">
        {control}
        {submitButton}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {control}
      <Button.Group>
        {submitButton}
        <Menu
          position="bottom-end"
          shadow="md"
          width={240}
          withinPortal
          onOpen={() => setSubmitAsOpened(true)}
        >
          <Menu.Target>
            <Button
              size="xs"
              px={6}
              aria-label="Submit as another practitioner"
              disabled={submitClaim.isPending}
            >
              <IconChevronDown size={14} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Submit as</Menu.Label>
            {practitionersPending ? (
              <Menu.Item disabled>Loading practitioners…</Menu.Item>
            ) : (
              practitioners
                .filter((practitioner) => practitioner.displayName)
                .map((practitioner) => {
                  // The rendering provider is the default path, marked rather than hidden.
                  const rendering = practitioner.displayName === item.display.practitionerName
                  return (
                    <Menu.Item
                      key={practitioner.practitionerId}
                      leftSection={
                        rendering ? (
                          <IconCheck size={14} />
                        ) : (
                          <span className="inline-block w-3.5" />
                        )
                      }
                      onClick={() => submit(rendering ? undefined : practitioner.practitionerId)}
                    >
                      {practitioner.displayName}
                    </Menu.Item>
                  )
                })
            )}
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    </div>
  )
}

/** InfiniteScroll needs a stable scrollable ancestor id (legacy pattern). */
const BILLING_V2_TABLE_SCROLL_CONTAINER_ID = 'billing-v2-table-scroll-container'

type BillingTableProps = {
  /** Accumulated pages (legacy-parity infinite scroll). */
  items: BillingCaseListItem[]
  isLoading: boolean
  /** Whether more rows exist beyond the loaded ones. */
  hasMore: boolean
  /** Next page is being fetched — shows the bottom loader row. */
  isLoadingMore: boolean
  /** Fetch the next page (fires as the scroll approaches the bottom). */
  onLoadMore: () => void
  /** Opens the Claim & Payment Details drawer for the row's case. */
  onOpenCase: (item: BillingCaseListItem) => void
  /** On-demand resync of the row's case from FHIR (POST /v2/billing/sync-case). */
  onSyncCase: (item: BillingCaseListItem) => Promise<void>
  /** Opens the billing-codes editor (chart's BillingEditModal) for the row's encounter. */
  onAddCodes: (item: BillingCaseListItem) => void
  /** Opens the appointment details modal, where the visit's insurance is attached/changed
   *  (legacy parity: the Insurance Status cell was the way in). */
  onOpenInsurance: (item: BillingCaseListItem) => void
  /** Rendered in an extra first header cell while the filter panel is collapsed. */
  filterToggle?: ReactNode
}

export const BillingTable = ({
  items,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onOpenCase,
  onSyncCase,
  onAddCodes,
  onOpenInsurance,
  filterToggle,
}: BillingTableProps) => {
  const navigate = useNavigate()
  const [syncingCaseId, setSyncingCaseId] = useState<number | null>(null)

  const handleSync = async (item: BillingCaseListItem) => {
    setSyncingCaseId(item.id)
    try {
      await onSyncCase(item)
    } finally {
      setSyncingCaseId(null)
    }
  }

  const columnCount = TABLE_HEADERS.length + 1 + (filterToggle ? 1 : 0)

  return (
    <Paper withBorder radius="md" className="relative min-h-0 flex-1 overflow-hidden shadow-sm">
      <LoadingOverlay
        visible={isLoading}
        zIndex={10}
        overlayProps={{ blur: 1 }}
        loaderProps={{
          style: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
        }}
      />
      <div id={BILLING_V2_TABLE_SCROLL_CONTAINER_ID} className="h-full overflow-auto">
        <InfiniteScroll
          dataLength={items.length}
          next={onLoadMore}
          hasMore={hasMore}
          scrollableTarget={BILLING_V2_TABLE_SCROLL_CONTAINER_ID}
          loader={
            isLoadingMore ? (
              <div className="flex justify-center py-4">
                <Loader size="sm" color="blue" />
              </div>
            ) : null
          }
          // The library wraps children in its own div; 'contents' keeps the table
          // participating in the OUTER container's scroll (the legacy fix — without
          // it vertical scrolling breaks inside the overflow-auto ancestor).
          style={{ display: 'contents' }}
        >
          <Table horizontalSpacing="sm" verticalSpacing="xs" className="min-w-[2100px]">
            <Table.Thead className="sticky top-0 z-10 bg-[#F8FBFF]">
              <Table.Tr>
                {filterToggle ? (
                  <Table.Th className="w-12 border-b border-slate-200 bg-[#F8FBFF] py-2">
                    {filterToggle}
                  </Table.Th>
                ) : null}
                {TABLE_HEADERS.map((header) => (
                  <Table.Th
                    key={header}
                    className="border-b border-slate-200 bg-[#F8FBFF] py-2 text-[11px] font-semibold tracking-[0.04em] text-slate-500 uppercase"
                  >
                    {header}
                  </Table.Th>
                ))}
                {/* Row actions (sync) — no header label. */}
                <Table.Th className="w-10 border-b border-slate-200 bg-[#F8FBFF] py-2" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.length > 0 ? (
                items.map((item) => (
                  <Table.Tr key={item.id} className="align-middle">
                    {filterToggle ? <Table.Td className="border-b border-slate-100 py-2" /> : null}
                    <Table.Td className="border-b border-slate-100 py-2">
                      {item.dos ? (
                        <Text size="sm" fw={600} c="dark">
                          {dayjs(item.dos).tz(BILLING_DISPLAY_TIMEZONE).format('MM/DD/YYYY')}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          N/A
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td
                      className="cursor-pointer border-b border-slate-100 py-2"
                      onClick={() => navigate(`/patients/${item.patientId}/demographics`)}
                    >
                      <Text size="sm" fw={700} c="dark" className="hover:underline">
                        {item.display.patientName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        DOB:{' '}
                        {item.display.patientDob
                          ? dayjs(item.display.patientDob).format('MM/DD/YYYY')
                          : 'N/A'}
                      </Text>
                    </Table.Td>

                    <Table.Td className="w-[300px] border-b border-slate-100 py-2">
                      {(() => {
                        const lines = addressLines(item.display.patientAddress)
                        if (!lines) {
                          return (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )
                        }
                        return (
                          <>
                            <Text size="sm" c="dark">
                              {lines[0]}
                            </Text>
                            {lines[1] ? (
                              <Text size="xs" c="dimmed">
                                {lines[1]}
                              </Text>
                            ) : null}
                          </>
                        )
                      })()}
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <VisitStatusCell item={item} />
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <Text size="sm" c="dark">
                        {item.display.practitionerName || '—'}
                      </Text>
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <Text size="sm" c="dark">
                        {item.display.insuranceName || '—'}
                      </Text>
                    </Table.Td>

                    <Table.Td
                      className="cursor-pointer border-b border-slate-100 py-2"
                      onClick={() => onOpenInsurance(item)}
                    >
                      <InsuranceStatusCell item={item} />
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <Text size="sm" c="dark">
                        {item.display.planName || '—'}
                      </Text>
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <Text size="sm" c="dark">
                        {memberIdDisplay(item.display.memberId)}
                      </Text>
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <BillingEntityControl
                        encounterId={item.encounterId}
                        entity={item.billingEntity}
                      />
                    </Table.Td>

                    <Table.Td
                      className="cursor-pointer border-b border-slate-100 py-2"
                      onClick={() => navigate(`/patients/${item.patientId}/progress-notes`)}
                    >
                      <ProgressNoteCell item={item} />
                    </Table.Td>

                    <Table.Td
                      className="cursor-pointer border-b border-slate-100 py-2"
                      onClick={() => onAddCodes(item)}
                    >
                      <Text
                        size="sm"
                        c={item.serviceLineCount === 0 ? 'dimmed' : 'dark'}
                        className="whitespace-nowrap hover:underline"
                      >
                        {formatBillingCodes(item)}
                      </Text>
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <RoutingCell item={item} />
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <PaymentsCell
                        paymentStatus={item.paymentStatus}
                        payments={item.payments}
                        selfPayStatus={item.display.selfPayStatus}
                        onOpen={() => onOpenCase(item)}
                      />
                    </Table.Td>

                    <Table.Td className="border-b border-slate-100 py-2">
                      <div className="flex items-center gap-1">
                        <ClaimStatusControl
                          encounterId={item.encounterId}
                          currentStatus={item.claimStatus}
                          hasPayments={
                            item.payments.insurancePaidCents > 0 ||
                            (item.payments.copayPaidCents ?? 0) > 0
                          }
                          managedRoute={item.claimSubmittedAt ? item.submissionRoute : undefined}
                        />
                        {item.submitLastError ? (
                          <Tooltip label={item.submitLastError} withArrow multiline w={260}>
                            <IconAlertCircle
                              size={16}
                              className="shrink-0 text-red-600"
                              aria-label="Needs attention"
                            />
                          </Tooltip>
                        ) : null}
                      </div>
                      {item.display.claimStatusUpdatedAt ? (
                        <Text size="xs" c="dimmed" className="mt-1">
                          {dayjs(item.display.claimStatusUpdatedAt)
                            .tz(BILLING_DISPLAY_TIMEZONE)
                            .format('MM/DD/YYYY')}
                        </Text>
                      ) : null}
                    </Table.Td>

                    <Table.Td className="w-10 border-b border-slate-100 py-2">
                      <Tooltip label="Sync from chart" withArrow>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label="Sync case"
                          loading={syncingCaseId === item.id}
                          onClick={() => void handleSync(item)}
                        >
                          <IconRefresh size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))
              ) : !isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={columnCount} className="py-10 text-center">
                    <Text size="sm" c="dimmed">
                      No billing cases found for the selected filters.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : null}
            </Table.Tbody>
          </Table>
        </InfiniteScroll>
      </div>
    </Paper>
  )
}
